import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import crmService from '../../../services/crmService';

// Get API base URL from environment or use relative path
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/erp/backend/api';

const AddLeadModal = ({ onClose, onLeadAdded }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCustomSkillLevel, setShowCustomSkillLevel] = useState(false);
  const [showCustomSource, setShowCustomSource] = useState(false);
  const [customSkillLevel, setCustomSkillLevel] = useState('');
  const [customSource, setCustomSource] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    skills: '',
    experience_years: 0,
    skill_level: 'entry',
    source: 'direct_contact',
    notes: '',
    // New fields for candidate salary expectations
    current_salary: '',
    expected_salary: '',
    notice_period: '',
    location_preferred: '',
    previous_company: '',
    previous_company_details: '',
    resume: null
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [resumePreview, setResumePreview] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF or Word document');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setResumeFile(file);
      setResumePreview(URL.createObjectURL(file));
    }
  };

  const removeResume = () => {
    setResumeFile(null);
    if (resumePreview) {
      URL.revokeObjectURL(resumePreview);
    }
    setResumePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // If there's a resume file, upload it first
      let resumeUrl = null;
      if (resumeFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', resumeFile);
        formDataUpload.append('type', 'resume');
        
        try {
          const uploadResponse = await fetch(`${API_BASE_URL}/upload`, {
            method: 'POST',
            body: formDataUpload,
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('erp_auth_token')}`
            }
          });
          
          if (uploadResponse.ok) {
            const uploadResult = await uploadResponse.json();
            resumeUrl = uploadResult.url || uploadResult.data?.url;
            console.log('Resume uploaded successfully:', resumeUrl);
          } else {
            const errorText = await uploadResponse.text();
            console.error('Resume upload failed:', errorText);
            setError('Failed to upload resume. Please try again.');
            setLoading(false);
            return;
          }
        } catch (uploadError) {
          console.error('Resume upload error:', uploadError);
          setError('Failed to upload resume. Please check your connection and try again.');
          setLoading(false);
          return;
        }
      }

      const leadData = {
        ...formData,
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
        resume_url: resumeUrl
      };

      const result = await crmService.createLead(leadData);

      if (result?.success) {
        onLeadAdded?.(result.data);
      } else {
        setError(result?.error || 'Failed to create lead');
      }
    } catch (error) {
      setError('An unexpected error occurred');
      console.log('Lead creation error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="UserPlus" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Add New Lead</h2>
              <p className="text-sm text-muted-foreground">Create a new applicant lead</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-center gap-2 text-destructive">
                  <Icon name="AlertCircle" size={16} />
                  <p className="font-medium">{error}</p>
                </div>
              </div>
            )}

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Full Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter candidate's full name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Phone Number *
                </label>
                <Input
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="+91-9876543210"
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="candidate@example.com"
                />
              </div>
            </div>

            {/* Professional Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Experience (Years)
                </label>
                <Input
                  type="number"
                  value={formData.experience_years}
                  onChange={(e) => handleInputChange('experience_years', parseInt(e.target.value) || 0)}
                  placeholder="0"
                  min="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Skill Level
                </label>
                <div className="flex gap-2">
                  {!showCustomSkillLevel ? (
                    <>
                      <Select
                        value={formData.skill_level}
                        onValueChange={(value) => handleInputChange('skill_level', value)}
                        className="flex-1"
                        options={[
                          { value: 'entry', label: 'Entry Level' },
                          { value: 'intermediate', label: 'Intermediate' },
                          { value: 'senior', label: 'Senior' },
                          { value: 'expert', label: 'Expert' }
                        ]}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowCustomSkillLevel(true)}
                        title="Add custom skill level"
                        className="flex-shrink-0"
                      >
                        <Icon name="Plus" size={16} />
                      </Button>
                    </>
                  ) : (
                    <div className="flex gap-2 flex-1">
                      <Input
                        value={customSkillLevel}
                        onChange={(e) => setCustomSkillLevel(e.target.value)}
                        placeholder="Enter custom skill level"
                        className="flex-1"
                        autoFocus
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          if (customSkillLevel.trim()) {
                            handleInputChange('skill_level', customSkillLevel.trim());
                          }
                          setShowCustomSkillLevel(false);
                          setCustomSkillLevel('');
                        }}
                        title="Use custom value"
                      >
                        <Icon name="Check" size={16} />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          setShowCustomSkillLevel(false);
                          setCustomSkillLevel('');
                        }}
                        title="Cancel"
                      >
                        <Icon name="X" size={16} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Lead Source
                </label>
                <div className="flex gap-2">
                  {!showCustomSource ? (
                    <>
                      <Select
                        value={formData.source}
                        onValueChange={(value) => handleInputChange('source', value)}
                        className="flex-1"
                        options={[
                          { value: 'direct_contact', label: 'Direct Contact' },
                          { value: 'job_portal', label: 'Job Portal' },
                          { value: 'referral', label: 'Referral' },
                          { value: 'website', label: 'Website' },
                          { value: 'social_media', label: 'Social Media' }
                        ]}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowCustomSource(true)}
                        title="Add custom source"
                        className="flex-shrink-0"
                      >
                        <Icon name="Plus" size={16} />
                      </Button>
                    </>
                  ) : (
                    <div className="flex gap-2 flex-1">
                      <Input
                        value={customSource}
                        onChange={(e) => setCustomSource(e.target.value)}
                        placeholder="Enter custom source"
                        className="flex-1"
                        autoFocus
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          if (customSource.trim()) {
                            handleInputChange('source', customSource.trim());
                          }
                          setShowCustomSource(false);
                          setCustomSource('');
                        }}
                        title="Use custom value"
                      >
                        <Icon name="Check" size={16} />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => {
                          setShowCustomSource(false);
                          setCustomSource('');
                        }}
                        title="Cancel"
                      >
                        <Icon name="X" size={16} />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Skills */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Skills
              </label>
              <Input
                value={formData.skills}
                onChange={(e) => handleInputChange('skills', e.target.value)}
                placeholder="JavaScript, React, Node.js, Python (comma-separated)"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter skills separated by commas
              </p>
            </div>

            {/* Salary & Availability Information */}
            <div className="border-t border-border pt-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Salary & Availability</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Current Salary
                  </label>
                  <Input
                    type="text"
                    value={formData.current_salary}
                    onChange={(e) => handleInputChange('current_salary', e.target.value)}
                    placeholder="e.g., ₹5,00,000 per annum"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Expected Salary
                  </label>
                  <Input
                    type="text"
                    value={formData.expected_salary}
                    onChange={(e) => handleInputChange('expected_salary', e.target.value)}
                    placeholder="e.g., ₹7,00,000 per annum"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Notice Period
                  </label>
                  <Select
                    value={formData.notice_period}
                    onValueChange={(value) => handleInputChange('notice_period', value)}
                    options={[
                      { value: 'immediate', label: 'Immediate' },
                      { value: '7_days', label: '7 Days' },
                      { value: '15_days', label: '15 Days' },
                      { value: '30_days', label: '30 Days' },
                      { value: '45_days', label: '45 Days' },
                      { value: '60_days', label: '60 Days' },
                      { value: '90_days', label: '90 Days' },
                      { value: 'negotiable', label: 'Negotiable' }
                    ]}
                    placeholder="Select notice period"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Location Preferred
                  </label>
                  <Input
                    value={formData.location_preferred}
                    onChange={(e) => handleInputChange('location_preferred', e.target.value)}
                    placeholder="e.g., Bangalore, Hyderabad, Remote"
                  />
                </div>
              </div>
            </div>

            {/* Previous Company Details */}
            <div className="border-t border-border pt-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Previous Company Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Previous Company Name
                  </label>
                  <Input
                    value={formData.previous_company}
                    onChange={(e) => handleInputChange('previous_company', e.target.value)}
                    placeholder="Enter previous company name"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Previous Company Details
                  </label>
                  <textarea
                    value={formData.previous_company_details}
                    onChange={(e) => handleInputChange('previous_company_details', e.target.value)}
                    placeholder="Enter details about previous employment (role, duration, etc.)"
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Resume Upload */}
            <div className="border-t border-border pt-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Resume</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Upload Resume (PDF or Word)
                </label>
                {!resumeFile ? (
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                    <input
                      type="file"
                      id="resume-upload"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={handleResumeChange}
                      className="hidden"
                    />
                    <label htmlFor="resume-upload" className="cursor-pointer">
                      <Icon name="Upload" size={24} className="mx-auto text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload resume (PDF or Word)
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Maximum file size: 5MB
                      </p>
                    </label>
                  </div>
                ) : (
                  <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <Icon name="FileText" size={24} className="text-primary" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{resumeFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(resumeFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={removeResume}
                      className="text-destructive hover:text-destructive"
                    >
                      <Icon name="Trash2" size={18} />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Add any additional notes about this candidate..."
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground resize-none"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              )}
              Create Lead
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddLeadModal;